import { Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/Dialog";

import { useState } from "react";
import { Allocation } from "@/api/SolarCloudApi";
import { Button } from "@/components/ui/Button";
import LinkCard from "./LinkCard";
import type { AllocationForm } from "./AddAllocationForm";

type DeleteAllocationDialogProps = {
  handleDeleteAllocation: (allocation: Allocation) => void;
  allocation: Allocation;
  account: "gas" | "electricity" | "bank" | undefined;
};

export default function DeleteAllocationDialog({
  handleDeleteAllocation,
  allocation,
  account,
}: DeleteAllocationDialogProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleDelete = () => {
    setIsOpen(false);
    handleDeleteAllocation(allocation);
  };

  // Convert allocation to form format for LinkCard
  const getFormValues = (allocation: Allocation): AllocationForm => {
    if (allocation.energyCompanyPayee) {
      return {
        paymentType: "energyCompany",
        nickname: allocation.nickname || "",
        wattCapacity: allocation.wattCapacity || 0,
        energyCompany: {
          uuid: allocation.energyCompanyPayee.energyCompany?.uuid || "",
          name: allocation.energyCompanyPayee.energyCompany?.name || "",
        },
        customerReferenceNumber:
          allocation.energyCompanyPayee.customerReferenceNumber || "",
        billerCode: allocation.energyCompanyPayee.billerCode || "",
      };
    }
    if (allocation.bankPayee) {
      return {
        paymentType: "bank",
        nickname: allocation.nickname || "",
        wattCapacity: allocation.wattCapacity || 0,
        accountName: allocation.bankPayee.accountName || "",
        bsb: allocation.bankPayee.bsb || "",
        accountNumber: allocation.bankPayee.accountNumber || "",
      };
    }
    if (allocation.payPalPayee) {
      return {
        paymentType: "paypal",
        nickname: allocation.nickname || "",
        wattCapacity: allocation.wattCapacity || 0,
        paypalEmail: allocation.payPalPayee.email || "",
      };
    }
    return {
      paymentType: "none",
      nickname: allocation.nickname || "",
      wattCapacity: allocation.wattCapacity || 0,
    };
  };

  const formValues = getFormValues(allocation);
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger>
        <div className="size-10 flex items-center justify-center bg-black rounded-lg border border-transparent hover:border-brand-orange-500">
          <Trash2 className="size-6 text-brand-orange-500" />
        </div>
      </DialogTrigger>
      <DialogContent className="bg-brand-neutral-800 md:w-[600px] overflow-y-scroll max-h-screen">
        <div className="w-full md:w-sm mx-auto flex flex-col gap-y-4">
          <DialogHeader>
            <DialogTitle>Delete {allocation.nickname}</DialogTitle>
          </DialogHeader>
          <DialogDescription>
            Are you sure you want to delete this link?
          </DialogDescription>

          <LinkCard
            formValues={formValues}
            account={account}
            showBpayWarning={false}
          />
          <div className="flex justify-end gap-4">
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              className="bg-red-600"
            >
              Delete
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
